import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import {
  AlertEvent,
  NotificationMember,
  ProjectNotificationSettings,
  TelegramChat,
  TelegramLink,
} from 'src/app/models/notifications.model';
import { ProjectManagementService } from 'src/app/services/project-management.service';
import { ToastService } from 'src/app/services/toast.service';

type Channel = 'email' | 'telegram';

interface EventOption {
  key: AlertEvent;
  label: string;
  description: string;
  icon: string;
}

// Fields the user edits; everything else in the settings payload is read-only.
const EDITABLE_FIELDS: (keyof ProjectNotificationSettings)[] = [
  'email_enabled',
  'email_recipients',
  'extra_emails',
  'email_events',
  'telegram_enabled',
  'telegram_events',
  'monitor_failure_threshold',
  'spike_minute_threshold',
  'spike_hour_threshold',
  'spike_multiplier',
  'spike_cooldown_minutes',
];

/** A deep copy of just the editable fields, so the form never shares arrays with saved state. */
function pickEditable(source: ProjectNotificationSettings): ProjectNotificationSettings {
  return JSON.parse(JSON.stringify(Object.fromEntries(EDITABLE_FIELDS.map(f => [f, source[f]]))));
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TELEGRAM_POLL_MS = 3000;
const TELEGRAM_POLL_LIMIT_MS = 5 * 60 * 1000;

@Component({
  selector: 'app-project-notifications',
  templateUrl: './project-notifications.component.html',
  styleUrls: ['./project-notifications.component.scss'],
  standalone: false,
})
export class ProjectNotificationsPageComponent implements OnInit, OnDestroy {
  readonly events: EventOption[] = [
    { key: 'monitor_down', label: 'Monitor down', description: 'A health check keeps failing.', icon: 'fa-arrow-circle-down' },
    { key: 'monitor_recovered', label: 'Monitor recovered', description: 'A failing check is healthy again.', icon: 'fa-check-circle' },
    { key: 'new_issue', label: 'New error', description: 'An error the project has never seen before.', icon: 'fa-bug' },
    { key: 'regression', label: 'Regression', description: 'A resolved error happens again.', icon: 'fa-undo' },
    { key: 'error_spike', label: 'Error spike', description: 'Errors per minute or hour jump above normal.', icon: 'fa-line-chart' },
  ];

  projectId = '';
  projectName = '';
  loading = true;
  loadError = false;
  saving = false;
  testing = false;

  form: ProjectNotificationSettings = null;
  private saved = '';

  members: NotificationMember[] = [];
  chats: TelegramChat[] = [];
  telegramAvailable = false;

  newEmail = '';
  newEmailError = '';

  telegramLink: TelegramLink = null;
  private pollTimer: ReturnType<typeof setInterval> = null;
  private pollStartedAt = 0;

  constructor(
    private route: ActivatedRoute,
    private projectService: ProjectManagementService,
    private toast: ToastService,
    private cdr: ChangeDetectorRef,
  ) { }

  async ngOnInit() {
    this.projectId = this.route.snapshot.paramMap.get('uuid');
    try {
      const [project, settings] = await Promise.all([
        lastValueFrom(this.projectService.getProject(this.projectId)),
        lastValueFrom(this.projectService.getNotificationSettings(this.projectId)),
      ]);
      this.projectName = project.name;
      this.apply(settings);
    } catch {
      this.loadError = true;
    }
    this.loading = false;
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    this.stopPolling();
  }

  get dirty(): boolean {
    return !!this.form && this.snapshot() !== this.saved;
  }

  // -- recipients ---------------------------------------------------------

  isRecipient(member: NotificationMember): boolean {
    return this.form.email_recipients.includes(member.uuid);
  }

  toggleRecipient(member: NotificationMember) {
    const recipients = this.form.email_recipients;
    this.form.email_recipients = this.isRecipient(member)
      ? recipients.filter(uuid => uuid !== member.uuid)
      : [...recipients, member.uuid];
  }

  addEmail() {
    const email = this.newEmail.trim().replace(/,$/, '');
    if (!email) return;
    if (!EMAIL_PATTERN.test(email)) {
      this.newEmailError = 'That doesn’t look like an email address.';
      return;
    }
    const known = [...this.form.extra_emails, ...this.members.filter(m => this.isRecipient(m)).map(m => m.email)];
    if (known.some(e => e?.toLowerCase() === email.toLowerCase())) {
      this.newEmailError = 'Already receiving alerts.';
      return;
    }
    this.form.extra_emails = [...this.form.extra_emails, email];
    this.newEmail = '';
    this.newEmailError = '';
  }

  onEmailKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      this.addEmail();
    }
  }

  removeEmail(email: string) {
    this.form.extra_emails = this.form.extra_emails.filter(e => e !== email);
  }

  get recipientCount(): number {
    return this.form.email_recipients.length + this.form.extra_emails.length;
  }

  // -- events -------------------------------------------------------------

  isOn(channel: Channel, event: AlertEvent): boolean {
    return this.eventsOf(channel).includes(event);
  }

  toggleEvent(channel: Channel, event: AlertEvent) {
    const current = this.eventsOf(channel);
    const next = current.includes(event) ? current.filter(e => e !== event) : [...current, event];
    // Keep the order stable so "dirty" doesn't flip on toggle/untoggle.
    const ordered = this.events.map(e => e.key).filter(key => next.includes(key));
    if (channel === 'email') this.form.email_events = ordered;
    else this.form.telegram_events = ordered;
  }

  private eventsOf(channel: Channel): AlertEvent[] {
    return channel === 'email' ? this.form.email_events : this.form.telegram_events;
  }

  // -- save / test --------------------------------------------------------

  async save(): Promise<boolean> {
    if (this.newEmail.trim()) this.addEmail();
    if (this.newEmailError) return false;

    this.saving = true;
    this.cdr.markForCheck();
    try {
      const settings = await lastValueFrom(
        this.projectService.updateNotificationSettings(this.projectId, pickEditable(this.form)),
      );
      this.apply(settings);
      this.toast.show('Notification settings saved', 'success');
      return true;
    } catch (err: any) {
      this.toast.show(this.describeError(err?.error) || 'Failed to save notification settings', 'error');
      return false;
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }

  discard() {
    this.form = JSON.parse(this.saved);
    this.newEmail = '';
    this.newEmailError = '';
  }

  async sendTest() {
    if (this.dirty && !(await this.save())) return;

    this.testing = true;
    this.cdr.markForCheck();
    try {
      const result = await lastValueFrom(this.projectService.sendTestAlert(this.projectId));
      const parts = Object.entries(result).map(([channel, r]) =>
        `${channel === 'email' ? 'Email' : 'Telegram'}: ${r.disabled ? 'paused platform-wide' : `${r.sent} sent${r.failed ? `, ${r.failed} failed` : ''}`}`);
      const failed = Object.values(result).some(r => r.failed || r.disabled);
      this.toast.show(parts.length ? `Test alert — ${parts.join(' · ')}` : 'No channel is enabled', failed ? 'error' : 'success');
    } catch {
      this.toast.show('Failed to send the test alert', 'error');
    } finally {
      this.testing = false;
      this.cdr.markForCheck();
    }
  }

  // -- telegram -----------------------------------------------------------

  async connectTelegram() {
    try {
      this.telegramLink = await lastValueFrom(this.projectService.createTelegramLink(this.projectId));
      this.startPolling();
    } catch {
      this.toast.show('Could not start the Telegram connection', 'error');
    }
    this.cdr.markForCheck();
  }

  cancelTelegram() {
    this.telegramLink = null;
    this.stopPolling();
  }

  async removeChat(chat: TelegramChat) {
    try {
      await lastValueFrom(this.projectService.deleteTelegramChat(this.projectId, chat.id));
      this.chats = this.chats.filter(c => c.id !== chat.id);
      this.toast.show(`Disconnected ${chat.title || 'chat'}`, 'success');
    } catch {
      this.toast.show('Failed to disconnect the chat', 'error');
    }
    this.cdr.markForCheck();
  }

  private startPolling() {
    this.stopPolling();
    const knownIds = new Set(this.chats.map(c => c.id));
    this.pollStartedAt = Date.now();

    this.pollTimer = setInterval(async () => {
      if (Date.now() - this.pollStartedAt > TELEGRAM_POLL_LIMIT_MS) {
        this.cancelTelegram();
        this.cdr.markForCheck();
        return;
      }
      try {
        const settings = await lastValueFrom(this.projectService.getNotificationSettings(this.projectId));
        const added = (settings.telegram_chats ?? []).filter(c => !knownIds.has(c.id));
        if (added.length) {
          this.chats = settings.telegram_chats;
          this.toast.show(`Telegram connected: ${added.map(c => c.title).join(', ')}`, 'success');
          this.cancelTelegram();
          this.cdr.markForCheck();
        }
      } catch {
        // Transient; the next tick tries again.
      }
    }, TELEGRAM_POLL_MS);
  }

  private stopPolling() {
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  // -- helpers ------------------------------------------------------------

  initials(member: NotificationMember): string {
    const source = member.name?.trim() || member.email || '?';
    const words = source.split(/\s+/).filter(Boolean);
    return (words.length > 1 ? words[0][0] + words[1][0] : source.slice(0, 2)).toUpperCase();
  }

  private apply(settings: ProjectNotificationSettings) {
    this.members = settings.members ?? [];
    this.chats = settings.telegram_chats ?? [];
    this.telegramAvailable = !!settings.telegram?.available;

    this.form = pickEditable(settings);
    this.saved = this.snapshot();
  }

  private snapshot(): string {
    return JSON.stringify(pickEditable(this.form));
  }

  private describeError(body: any): string {
    if (!body || typeof body !== 'object') return '';
    const [field, messages] = Object.entries(body)[0] ?? [];
    const message = Array.isArray(messages) ? messages.find(m => typeof m === 'string') ?? JSON.stringify(messages) : messages;
    return field ? `${String(field).replace(/_/g, ' ')}: ${message}` : '';
  }
}
